import React from 'react';
import cn from 'classnames';
import { Trans } from '@lingui/macro';
import { Badge, Button } from '@economic/taco';

import Checkmark from './checkmark.svg';
import ConfirmSubscriptionChangeDialog from './ConfirmSubscriptionChangeDialog';
import ExternalLink from '../../../../shared/components/ExternalLink';
import { ChangeSubscriptionPlan } from './types';
import { Column, Row } from '../../../../shared/components/Grid/Grid';

function Prices({ prices }: { prices: ChangeSubscriptionPlan }) {
    const { packagePrice, transactionPrice, addonPrice, userPrice } = prices;

    return (
        <div className="mb-3 mt-3 pb-3 pt-3 bg-grey-lightest">
            <div className="pl-4 pr-4">
                <Row>
                    <Column xs="8">
                        <Trans>Package price</Trans>
                    </Column>
                    <Column>
                        <div className="text-right">{Math.round(packagePrice)},-</div>
                    </Column>
                </Row>
                <Row>
                    <Column xs="8">
                        <Trans>Transaction price</Trans>
                    </Column>
                    <Column>
                        <div className="text-right">+ {Math.round(transactionPrice)},-</div>
                    </Column>
                </Row>
                <Row>
                    <Column xs="8">
                        <Trans>Users</Trans>
                    </Column>
                    <Column>
                        <div className="text-right">+ {Math.round(userPrice)},-</div>
                    </Column>
                </Row>
                <Row>
                    <Column xs="8">
                        <Trans>Add-ons</Trans>
                    </Column>
                    <Column>
                        <div className="text-right">+ {Math.round(addonPrice)},-</div>
                    </Column>
                </Row>
                <div className="w-8 border border-grey-dark mt-2 mb-2" />
                <div className="font-bold text-lg">
                    <Row>
                        <Column xs="8">
                            <Trans>Total price</Trans>
                        </Column>
                        <Column>
                            <div className="text-right">
                                {Math.round(packagePrice + transactionPrice + addonPrice + userPrice)},-
                            </div>
                        </Column>
                    </Row>
                </div>
            </div>
        </div>
    );
}

function Functionality({
    functionalityHeader,
    functionalities,
}: {
    functionalityHeader: string;
    functionalities: { text: string; header?: boolean }[];
}) {
    return (
        <div className="pl-4">
            <div className="mb-3 h-6">{functionalityHeader}</div>
            <div className="text-xs h-72">
                {functionalities.map((functionality, i) => (
                    <div className="flex leading-4 mb-3 items-center" key={i}>
                        {functionality.header ? (
                            <span className="ml-2 pt-2 pb-1">{functionality.text}</span>
                        ) : (
                            <>
                                <img className="ml-2 mr-2" src={Checkmark} />
                                {functionality.text}
                            </>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function PackageCard({
    planId,
    header,
    isDowngrade,
    contentText,
    readMoreLink,
    prices,
    functionalityHeader,
    functionalities,
    isTrialUpgrade,
    size,
    className = '',
    highlighted = false,
    disabled = false,
    disabledReason = undefined,
}: {
    planId: number;
    header: string;
    isDowngrade: boolean;
    contentText: string;
    readMoreLink: string | undefined;
    prices: ChangeSubscriptionPlan;
    functionalityHeader: string;
    functionalities: { text: string; header?: boolean }[];
    isTrialUpgrade: boolean;
    size: 'sm' | 'md';
    className?: string | undefined;
    highlighted: boolean;
    disabled: boolean;
    disabledReason?: string | undefined;
}) {
    const sizeClasses = size === 'md' ? 'w-64 min-w-64' : 'w-56 min-w-56';
    return (
        <div
            className={cn('pt-4 pb-4 rounded-xl', className, sizeClasses)}
            style={{ boxShadow: '0px 0px 15px 1px rgba(0, 0, 0, 0.1)' }}>
            <div className="mb-6 pr-4">
                {highlighted && (
                    <Badge state="information" className="float-right">
                        {!isTrialUpgrade ? <Trans>Current</Trans> : <Trans>Suggested</Trans>}
                    </Badge>
                )}
            </div>
            <h3 className="font-bold pl-4">{header}</h3>
            <div className="h-32 pl-4 pr-4">
                {contentText}
                {readMoreLink && (
                    <div className="text-xs mt-2">
                        <ExternalLink href={readMoreLink}>
                            <Trans>Read more</Trans>
                        </ExternalLink>
                    </div>
                )}
            </div>
            <Prices prices={prices} />
            <Functionality functionalityHeader={functionalityHeader} functionalities={functionalities} />
            <div className="flex justify-center pb-6 pl-4 pr-4">
                {disabledReason ? (
                    <div className="text-xs text-center italic">{disabledReason}</div>
                ) : highlighted ? (
                    !isTrialUpgrade ? (
                        <Button disabled>
                            <Trans>Current plan</Trans>
                        </Button>
                    ) : (
                        <Button
                            appearance="primary"
                            dialog={dialogProps => (
                                <ConfirmSubscriptionChangeDialog
                                    modalProps={dialogProps}
                                    planName={header}
                                    planId={planId}
                                    isDowngrade={isDowngrade}
                                    isTrialUpgrade={isTrialUpgrade}
                                    readMoreLink={readMoreLink}
                                />
                            )}>
                            <Trans>Select suggested</Trans>
                        </Button>
                    )
                ) : disabled /* If no disabledReason has been provided just disable the button */ ? (
                    <Button disabled>
                        <Trans>Select plan</Trans>
                    </Button>
                ) : (
                    <Button
                        appearance="ghost"
                        dialog={dialogProps => (
                            <ConfirmSubscriptionChangeDialog
                                modalProps={dialogProps}
                                planName={header}
                                planId={planId}
                                isDowngrade={isDowngrade}
                                isTrialUpgrade={isTrialUpgrade}
                                readMoreLink={readMoreLink}
                            />
                        )}>
                        <Trans>Select plan</Trans>
                    </Button>
                )}
            </div>
        </div>
    );
}
